import { InitialCarePlan } from '@seen/core/models/care-plans/InitialCarePlan'
import { Badge, type BadgeProps, TypographyPageTitle } from '@seen/ui'
import { orderBy } from 'lodash-es'
import type { MRT_TableOptions } from 'mantine-react-table'
import { type JSX, useMemo } from 'react'
import { useNavigate } from 'react-router'
import { carePlanStateColors } from '#src/components/care-plan/constants.ts'
import { CreateCarePlanButton } from '#src/components/care-plan/CreateCarePlanButton.tsx'
import { HoverDate } from '#src/components/core/hover-card/HoverDate.tsx'
import { Link } from '#src/components/core/Link.tsx'
import { Loader } from '#src/components/core/Loader.tsx'
import type { ColumnConfig } from '#src/components/fhir-table/FhirMRTTable.constants.tsx'
import { ReactTable } from '#src/components/fhir-table/ReactTable.tsx'
import { useCurrentParticipant } from '#src/contexts/current-participant/useCurrentParticipant.ts'
import { useNavigateFromClickEvent } from '#src/hooks/useNavigateFromClickEvent.ts'
import { useSearch } from '#src/hooks/useSearch.ts'

export function CarePlanHistoryPage(): JSX.Element {
  const navigate = useNavigate()
  const participant = useCurrentParticipant()

  const carePlansQuery = useSearch(
    InitialCarePlan,
    InitialCarePlan.byParticipant(participant.reference)
  )

  const carePlans = useMemo(
    () => orderBy([...(carePlansQuery.data ?? [])], ['lastUpdated'], ['desc']),
    [carePlansQuery.data]
  )

  const navigateFromClickEvent = useNavigateFromClickEvent()
  const columnConfigs = useMemo<ColumnConfig<InitialCarePlan>[]>(
    () => [
      {
        id: 'title',
        headerText: 'Care Plan',
        render: (carePlan) => (
          <Link to={`../${carePlan.id}`}>{carePlan.title ?? 'Care Plan'}</Link>
        ),
        valueType: 'text' as const,
        filteringType: 'text',
        getSortFilterValue: (carePlan) => carePlan.title,
        enableSorting: true,
      },
      {
        id: 'status',
        headerText: 'Status',
        render: (carePlan) => {
          const color = carePlanStateColors[
            carePlan.state
          ] as BadgeProps['color']
          return (
            <div className="flex justify-start">
              <Badge variant="outline" color={color}>
                {carePlan.state}
              </Badge>
            </div>
          )
        },
        valueType: 'text' as const,
        filteringType: 'text',
        getSortFilterValue: (carePlan) => carePlan.status,
        enableSorting: true,
        useAsInitialSort: true,
      },
      {
        id: 'category',
        headerText: 'Type',
        render: (carePlan) => <>{carePlan.typeDisplay}</>,
        valueType: 'text' as const,
        filteringType: 'text',
        getSortFilterValue: (carePlan) => carePlan.type,
        enableSorting: true,
      },
      {
        id: 'start-date',
        headerText: 'Start Date',
        render: (carePlan) =>
          carePlan.startDate ? <HoverDate date={carePlan.startDate} /> : null,
        valueType: 'date' as const,
        filteringType: 'date-range',
        getSortFilterValue: (carePlan) => carePlan.startDate,
        enableSorting: true,
      },
      {
        id: 'end-date',
        headerText: 'End Date',
        render: (carePlan) =>
          carePlan.endDate ? <HoverDate date={carePlan.endDate} /> : null,
        valueType: 'date',
        filteringType: 'date-range',
        getSortFilterValue: (carePlan) => carePlan.endDate,
        enableSorting: true,
      },
      {
        id: 'last-updated',
        headerText: 'Last Updated',
        render: (carePlan) => (
          <HoverDate date={carePlan.lastUpdated} format="relative" />
        ),
        valueType: 'date' as const,
        filteringType: 'date-range',
        getSortFilterValue: (carePlan) => carePlan.lastUpdatedDate,
        enableSorting: true,
      },
    ],
    []
  )

  const tableOptions = useMemo(
    () =>
      ({
        mantineTableBodyRowProps: ({ row }) => ({
          onClick: (event) => {
            navigateFromClickEvent(`../${row.original.id}`, event)
          },
          className: 'cursor-pointer',
        }),

        getRowId: (row) => row.id,
      }) as Partial<MRT_TableOptions<InitialCarePlan>>,
    [navigateFromClickEvent]
  )

  if (carePlansQuery.isLoading) {
    return <Loader />
  }

  return (
    <div className="grid gap-6">
      <div className="flex items-center justify-between gap-4">
        <TypographyPageTitle>Care Plan History</TypographyPageTitle>
        <CreateCarePlanButton
          participant={participant.reference}
          onCreated={(carePlanId) => {
            void navigate(`/Patient/${participant.id}/care-plans/${carePlanId}`)
          }}
        />
      </div>
      <div className="overflow-auto">
        <ReactTable
          columnConfigs={columnConfigs}
          data={carePlans}
          tableOptions={tableOptions}
          tableName="care-plan-history"
        />
      </div>
    </div>
  )
}
